import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { LogoFull } from "./Logo";
import { TUBE_ROUTES } from "../data/productPages";

const TUBE_LINKS = [
  { label: "Butt Jointed Tubes", href: TUBE_ROUTES["butt-joint"] },
  { label: "Telescopic Tubes", href: TUBE_ROUTES["telescopic"] },
  { label: "Metal Lid Tubes", href: TUBE_ROUTES["tin-lid"] },
];

const NAV = [
  { label: "Sizes", href: "#sizes" },
  { label: "Finishes", href: "#finishes" },
  { label: "Quote", href: "#quote" },
];

export const Header = ({ announcement }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [tubesOpen, setTubesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "bg-[#0B0D10]/85 border-b border-amber-500/15" : "bg-[#0B0D10]/40 border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      {announcement && (
        <div className="bg-gradient-to-r from-[#A67A43]/20 via-[#D4AF37]/15 to-[#A67A43]/20 border-b border-amber-500/10">
          <p className="text-center font-mono-spec text-[10px] uppercase tracking-[0.25em] text-[#D9A76A] py-1.5 px-4" data-testid="announcement-bar">
            {announcement}
          </p>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo-link" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <LogoFull compact />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <div
            className="relative"
            onMouseEnter={() => setTubesOpen(true)}
            onMouseLeave={() => setTubesOpen(false)}
          >
            <button
              onClick={() => setTubesOpen(!tubesOpen)}
              aria-expanded={tubesOpen}
              aria-haspopup="true"
              data-testid="nav-tubes-dropdown"
              className="flex items-center gap-1.5 text-[13px] font-medium tracking-wide text-[#A1A8B3] hover:text-[#D4AF37] transition-colors duration-300"
            >
              Tubes <ChevronDown size={13} className={`transition-transform duration-300 ${tubesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {tubesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                  data-testid="nav-tubes-menu"
                >
                  <div className="bg-[#14171D] border border-amber-500/15 rounded-2xl p-2 w-60 shadow-2xl">
                    {TUBE_LINKS.map((t) => (
                      <Link
                        key={t.href}
                        to={t.href}
                        onClick={() => setTubesOpen(false)}
                        data-testid={`nav-tubes-${t.href.split("/").pop()}`}
                        className="block px-4 py-3 rounded-xl text-[13px] text-[#A1A8B3] hover:text-[#E6CA65] hover:bg-white/5 transition-colors"
                      >
                        {t.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV.map((item) => (
            <button
              key={item.href}
              onClick={() => goTo(item.href)}
              data-testid={`nav-${item.label.toLowerCase()}-link`}
              className="text-[13px] font-medium tracking-wide text-[#A1A8B3] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => goTo("#quote")}
            data-testid="nav-quote-cta"
            className="gold-btn text-[#0B0D10] text-[13px] font-semibold px-5 py-2.5 rounded-full"
          >
            Get a Quote
          </button>
        </nav>
        <button
          className="md:hidden text-[#F5F3EF] p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#0B0D10]/95 border-b border-amber-500/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              <p className="font-mono-spec text-[10px] uppercase tracking-[0.25em] text-[#666F7D]">Tubes</p>
              {TUBE_LINKS.map((t) => (
                <Link
                  key={t.href}
                  to={t.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-tubes-${t.href.split("/").pop()}`}
                  className="text-[15px] font-medium text-[#F5F3EF] pl-3"
                >
                  {t.label}
                </Link>
              ))}
              {NAV.map((item) => (
                <button
                  key={item.href}
                  onClick={() => goTo(item.href)}
                  data-testid={`nav-mobile-${item.label.toLowerCase()}-link`}
                  className="text-left text-[15px] font-medium text-[#F5F3EF]"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
